import React, {
  FunctionComponent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Container,
  DropdownItemProps,
  DropdownProps,
  Form,
  FormField as FormFieldUi,
  Select,
  Transition,
} from 'semantic-ui-react';
import { getIn, useFormikContext } from 'formik';
import Label from 'components/label/label';
import HelpText from 'components/help-text/help-text';

const CUSTOM_OPTION_VALUE = 'custom';

export type Suggestion = {
  key: string | number;
  label: string | ReactNode;
  fields: Record<string, string | number | undefined>;
};

/** Compute the selected dropdown value from suggestions + Formik values. */
const findMatchingSuggestion = (
  suggestions: Suggestion[] | undefined,
  values: unknown,
): string | undefined => {
  if (!suggestions || suggestions.length === 0) return undefined;

  const matchIndex = suggestions.findIndex((suggestion) =>
    Object.entries(suggestion.fields).every(
      ([fieldName, fieldValue]) => getIn(values, fieldName) === fieldValue,
    ),
  );

  if (matchIndex >= 0) return `suggestion-${matchIndex}`;

  const hasAnyValue = Object.keys(suggestions[0].fields).some((fieldName) => {
    const val = getIn(values, fieldName);
    return val !== undefined && val !== null && val !== '';
  });

  return hasAnyValue ? CUSTOM_OPTION_VALUE : undefined;
};

type FieldSuggestionsProps = {
  label: string;
  required?: boolean;
  helpText?: string;
  placeholder?: string;
  customOptionLabel?: string;
  suggestions?: Suggestion[];
  children: ReactNode;
};

const FieldSuggestions: FunctionComponent<FieldSuggestionsProps> = ({
  label: fieldLabel,
  required,
  helpText,
  placeholder,
  customOptionLabel = 'Personnalisé',
  suggestions,
  children,
}) => {
  const { setFieldValue, values, errors, touched } = useFormikContext();

  const hasSuggestions = suggestions && suggestions.length > 0;

  const [selectedValue, setSelectedValue] = useState<string | undefined>(() =>
    findMatchingSuggestion(suggestions, values),
  );

  const userSelected = useRef(false);

  // Sync selected value when suggestions become available asynchronously
  useEffect(() => {
    if (userSelected.current) return;
    setSelectedValue(findMatchingSuggestion(suggestions, values));
  }, [suggestions]);

  // Build dropdown options from suggestions
  const options: DropdownItemProps[] = useMemo(() => {
    if (!hasSuggestions) return [];

    const otpions: DropdownItemProps[] = suggestions.map(
      (suggestion, index) => ({
        key: suggestion.key,
        text: suggestion.label,
        value: `suggestion-${index}`,
      }),
    );

    otpions.push({
      key: 'custom',
      text: customOptionLabel,
      value: CUSTOM_OPTION_VALUE,
    });

    return otpions;
  }, [suggestions, customOptionLabel]);

  const isCustom = selectedValue === CUSTOM_OPTION_VALUE;

  const handleChange = (
    event: React.SyntheticEvent<HTMLElement>,
    data: DropdownProps,
  ): void => {
    const value = data.value as string | undefined;
    if (value === undefined || value === null) return;

    userSelected.current = true;
    setSelectedValue(value);

    if (value === CUSTOM_OPTION_VALUE) {
      // Custom selected: clear all fields so the user can type
      if (suggestions && suggestions.length > 0) {
        Object.keys(suggestions[0].fields).forEach((fieldName) => {
          setFieldValue(fieldName, '');
        });
      }
      return;
    }

    // A suggestion was selected: apply its field values
    const suggestionIndex = parseInt(value.replace('suggestion-', ''), 10);

    if (suggestions && suggestions[suggestionIndex]) {
      Object.entries(suggestions[suggestionIndex].fields).forEach(
        ([fieldName, fieldValue]) => {
          setFieldValue(fieldName, fieldValue);
        },
      );
    }
  };

  const hasError = useMemo(() => {
    if (!hasSuggestions) return false;
    return Object.keys(suggestions[0].fields).some((fieldName) => {
      return !!getIn(errors, fieldName) && !!getIn(touched, fieldName);
    });
  }, [hasSuggestions, suggestions, errors, touched]);

  // No suggestions: render children directly with label and indentation
  if (!hasSuggestions) {
    return (
      <>
        <FormFieldUi>
          <Label required={required}>{fieldLabel}</Label>
          {helpText ? <HelpText>{helpText}</HelpText> : null}
        </FormFieldUi>
        <Container fluid style={{ paddingLeft: '50px' }}>
          <Form.Group widths="equal">{children}</Form.Group>
        </Container>
      </>
    );
  }

  return (
    <>
      <FormFieldUi>
        <Label required={required}>{fieldLabel}</Label>
        {helpText ? <HelpText>{helpText}</HelpText> : null}
        <Select
          error={hasError}
          onChange={handleChange}
          placeholder={placeholder}
          options={options}
          value={selectedValue}
        />
      </FormFieldUi>
      <Transition.Group duration={250}>
        {isCustom ? (
          <Container fluid style={{ paddingLeft: '50px' }}>
            <Form.Group widths="equal">{children}</Form.Group>
          </Container>
        ) : null}
      </Transition.Group>
    </>
  );
};

export default FieldSuggestions;
